import { HStack, VStack, Box } from '@cad-arc';
import React from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { ContactForm } from './contact-form';
import { ContactDetails } from './contact-details';
import { ContactListPanelState, ContactListItem } from './types';
import { ContactListProfileView, RolodexResource } from '@mark43/cad-api';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';

export function ContactListRightPane(props: {
    panelState: ContactListPanelState;
    setPanelState: React.Dispatch<ContactListPanelState>;
    setContact: (contact: ContactListItem) => void;
    removeContact: (id: number) => void;
}): React.ReactElement {
    const apiClient = useApiClient();
    const form = useForm<ContactListProfileView>({
        defaultValues: {
            name: '',
            rolodexProfileTypeAttr: undefined,
            address: '',
            primaryRolodexContact: {
                name: '',
                phoneNumber: '',
                note: '',
            },
            secondaryRolodexContacts: [],
        },
        mode: 'onBlur',
    });
    const query = useQuery(
        ['contact-list-form', props.panelState.selectedContactId] as const,
        ({ queryKey }) => {
            return apiClient
                .request<RolodexResource.GetContactListProfileById>({
                    method: 'GET',
                    url: `rolodex/contact_list/${queryKey[1]}/`,
                })
                .then((response) => {
                    if (response.data.data) {
                        return response.data.data;
                    }
                });
        },
        {
            enabled: !!props.panelState.selectedContactId,
        }
    );
    React.useEffect(() => {
        if (query.data) {
            form.reset(query.data);
        }
    }, [form, query.data]);
    const saveMutation = useMutation(
        (args: ContactListProfileView) => {
            return apiClient
                .request<RolodexResource.UpsertContactListProfile>({
                    method: 'POST',
                    url: 'rolodex/contact_list/',
                    data: args,
                })
                .then((response) => {
                    return response.data.data;
                });
        },
        {
            onSuccess(data) {
                if (data) {
                    props.setContact({
                        id: data.id,
                        orgName: data.name,
                        primaryContactName: data.primaryRolodexContact?.name,
                        primaryContactPhoneNumber: data.primaryRolodexContact?.phoneNumber,
                    });
                    props.setPanelState({
                        formMode: 'VIEW',
                        selectedContactId: data.id,
                    });
                }
            },
        }
    );
    return (
        <IntegratedFormProvider mutation={saveMutation} query={query} form={form}>
            <VStack h="100%" w="100%" p={2} justifyContent="space-between" alignItems="flex-start">
                <Box overflowY="auto" w="100%" flexGrow={100}>
                    <FormBody>
                        {props.panelState.formMode === 'CREATE' ||
                        props.panelState.formMode === 'EDIT' ? (
                            <ContactForm form={form} />
                        ) : (
                            <ContactDetails
                                form={form}
                                setPanelState={props.setPanelState}
                                removeContact={props.removeContact}
                            />
                        )}
                    </FormBody>
                </Box>
                {props.panelState.formMode === 'EDIT' || props.panelState.formMode === 'CREATE' ? (
                    <HStack
                        w="100%"
                        justifyContent="flex-end"
                        flexShrink={0}
                        p={4}
                        borderTop="sm"
                        borderColor="border.default"
                    >
                        <CancelAndSaveButtons form={form} setPanelState={props.setPanelState} />
                    </HStack>
                ) : (
                    <></>
                )}
            </VStack>
        </IntegratedFormProvider>
    );
}

function CancelAndSaveButtons(props: {
    form: UseFormReturn<ContactListProfileView, any>;
    setPanelState: React.Dispatch<ContactListPanelState>;
}): React.ReactElement {
    return (
        <>
            <FormCancelButton
                onClick={() => {
                    props.form.reset();
                    const contactId = props.form.getValues().id;
                    if (contactId) {
                        props.setPanelState({
                            formMode: 'VIEW',
                            selectedContactId: contactId,
                        });
                    } else {
                        props.setPanelState({
                            formMode: 'HIDDEN',
                            selectedContactId: undefined,
                        });
                    }
                }}
            />
            <FormSaveButton />
        </>
    );
}
